import { Form, Space, TableProps, Tag } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { EyeIcon } from 'assets/svg';
import { ButtonAction } from 'components';
import TableComponent from 'components/Table/TableComponent';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { TEAM_TYPE } from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';
import React, { forwardRef, memo, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import path from 'routes/Path';
import formatNumber, {
  formatCurrency,
  formatDate,
  generateUUID,
} from 'utils/constants/functions';
import { HocChangePagination } from 'utils/HocChangePagination';

import {
  IBillPaymentRecord,
  IBillPaymentListData,
  ITableV2Handle,
} from './types';

interface IProps extends React.PropsWithChildren<TableProps> {
  data: IBillPaymentListData;
  refetch: (_isExport?: boolean, _headerRow?: unknown) => void;
}

const TablePaymentSummaryV2 = forwardRef(
  (props: IProps, ref: React.ForwardedRef<ITableV2Handle>) => {
    const { data = { items: [] }, loading, refetch } = props;
    const [form] = useForm();
    const navigate = useNavigate();

    const columns: ColumnsTypeCustom = [
      {
        title: 'STT',
        width: 50,
        align: 'center',
        render: (_, __, index) =>
          (data.pageInfo?.itemsPerPage || 20) *
            ((data.pageInfo?.currentPage || 1) - 1) +
          index +
          1,
      },
      {
        title: 'Mã THTT',
        width: 130,
        dataIndex: 'id',
        render: (val, record: any) => (
          <div className="flex items-center gap-2">
            <span>{val}</span>
            {record.version === 'V2' && (
              <span
                style={{
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 500,
                  backgroundColor: '#e6f4ff',
                  color: '#1677ff',
                  border: '1px solid #91caff',
                }}
              >
                QR
              </span>
            )}
          </div>
        ),
      },
      {
        title: 'Khách hàng',
        width: 150,
        dataIndex: ['tcustomer', 'sapCustomerId'],
        render: (val, record) =>
          record?.tcustomer?.sapCustomerId || record?.customerId || '-',
      },
      {
        title: 'Tên Khách hàng',
        width: 180,
        dataIndex: ['tcustomer', 'storeName'],
        render: (val, record) =>
          record?.tcustomer?.storeName || record?.tcustomer?.name || '-',
      },
      {
        title: 'Mã KH cha',
        width: 150,
        dataIndex: ['tcustomer', 'mainCustomerId'],
        render: (val, record) => record?.tcustomer?.mainCustomerId || '-',
      },
      {
        title: 'Loại đội',
        width: 100,
        dataIndex: ['tteamSale', 'teamType'],
        render: (val, record) => {
          const tt =
            record?.tteamSale?.teamType || record?.tteamSales?.teamType;
          return !tt
            ? 'Kế toán'
            : tt === TEAM_TYPE.stay
              ? 'Tại chỗ'
              : 'Chào hàng';
        },
      },
      {
        title: 'Mã đội',
        width: 100,
        dataIndex: ['tteamSale', 'subId'],
        render: (val, record) =>
          record?.tteamSale?.subId || record?.tteamSales?.subId || '-',
      },
      {
        title: 'Tên đội',
        width: 150,
        dataIndex: ['tteamSale', 'name'],
        render: (val, record) =>
          record?.tteamSale?.name || record?.tteamSales?.name || '-',
      },
      {
        title: 'Giá bóng',
        width: 100,
        dataIndex: 'marketPrice',
        align: 'right',
        type: 'number',
        render: (value) => formatNumber(value),
      },
      {
        title: 'Tổng thanh toán',
        width: 150,
        dataIndex: 'finalPrice',
        align: 'right',
        type: 'number',
        render: (value) => formatNumber(value),
      },
      {
        title: 'KH ghi nợ',
        width: 100,
        dataIndex: 'customerDebitId',
        align: 'right',
        type: 'number',
      },
      {
        title: 'KH nợ:Q10',
        width: 100,
        dataIndex: 'paymentCustomerDebitQ10',
        align: 'right',
        type: 'number',
        render: (value) =>
          formatCurrency({
            value: Number(Number(value).toFixed(4)),
            format: 4,
          }) || '_',
      },
      {
        title: 'KH nợ: Tiền mặt',
        width: 170,
        dataIndex: 'paymentCustomerDebitAmount',
        align: 'right',
        type: 'number',
        render: (value) => formatNumber(value),
      },
      {
        title: 'PNJ nợ: Q10',
        width: 130,
        dataIndex: 'paymentPnjpDebitQ10',
        align: 'right',
        type: 'number',
        render: (value) =>
          formatCurrency({
            value: Number(Number(value).toFixed(4)),
            format: 4,
          }) || '_',
      },
      {
        title: 'PNJ nợ: Tiền mặt',
        width: 170,
        dataIndex: 'pnjpDebitAmount',
        align: 'right',
        type: 'number',
        render: (value) => formatNumber(value),
      },
      {
        title: 'Chi nhánh',
        width: 100,
        dataIndex: ['tplant', 'id'],
      },
      {
        title: 'Trạng thái',
        width: 140,
        dataIndex: ['tstatus', 'name'],
        render: (val, record: any) => {
          const statusStr = val || record?.status || 'Mới tạo';
          let color = 'default';
          if (statusStr === 'Đã Thanh Toán' || statusStr === 'Thành công')
            color = 'success';
          else if (
            statusStr === 'Thanh toán 1 phần' ||
            statusStr === 'Thanh toán 1 phần' ||
            statusStr === 'Đã gửi SAP (1 phần)'
          )
            color = 'warning';
          else if (statusStr === 'Thất Bại') color = 'error';
          return <Tag color={color}>{statusStr}</Tag>;
        },
      },
      {
        title: 'Trạng thái gửi (SAP)',
        width: 160,
        align: 'center',
        dataIndex: 'sapStatus',
        render: (val, record: any) => {
          const details = record?.tsumPaymentDetails || [];
          let st = val;
          if (!st && details.length > 0) {
            if (details.every((d: any) => d.sapStatus === 'SUCCESS'))
              st = 'SUCCESS';
            else if (details.some((d: any) => d.sapStatus === 'FAILED'))
              st = 'FAILED';
            else if (
              details.some(
                (d: any) =>
                  d.sapStatus === 'SUCCESS' ||
                  d.sapStatus === 'PARTIAL_SUCCESS',
              )
            )
              st = 'PARTIAL_SUCCESS';
            else st = 'PENDING';
          }
          st = st || 'PENDING';
          if (st === 'SUCCESS' || st === 'Thành công')
            return <Tag color="success">Thành công</Tag>;
          if (
            st === 'PARTIAL_SUCCESS' ||
            st === 'PARTIAL' ||
            st === 'Đã gửi SAP'
          )
            return <Tag color="warning">Đã gửi SAP</Tag>;
          if (st === 'FAILED' || st === 'Thất bại')
            return <Tag color="error">Thất bại</Tag>;
          return <Tag color="default">Chưa gửi</Tag>;
        },
      },
      {
        title: 'Người tạo',
        width: 170,
        dataIndex: ['tcreateUser', 'fullName'],
        render: (val, record) =>
          record?.tcreateUser?.fullName ||
          record?.createName ||
          record?.createId ||
          '-',
      },
      {
        title: 'Ngày tạo',
        width: 170,
        type: 'date',
        dataIndex: 'createDate',
        render: (value) => (value ? formatDate(value) : '-'),
      },
      {
        title: 'Người cập nhật',
        width: 170,
        dataIndex: ['tmodifyUser', 'fullName'],
        render: (val, record) =>
          record?.tmodifyUser?.fullName ||
          record?.modifyName ||
          record?.modifyId ||
          '-',
      },
      {
        title: 'Ngày cập nhật',
        width: 170,
        type: 'date',
        dataIndex: 'modifyDate',
        render: (value) => (value ? formatDate(value) : '-'),
      },
      {
        fixed: 'right',
        align: 'center',
        width: 70,
        render: (__, record: IBillPaymentRecord) => (
          <Space size={'middle'} className="text-lg ">
            <ButtonAction
              tooltip="Xem chi tiết"
              onClick={() => {
                const targetPath =
                  (record as any)?.version === 'V1'
                    ? path.PAYMENT_SUMMARY_DETAIL
                    : path.PAYMENT_SUMMARY_DETAIL_V2;
                navigate(targetPath, {
                  state: {
                    sumPaymentId: record?.id,
                    customerId: record?.tcustomer?.id,
                    ...(record?.tteamSale
                      ? {
                          teamId: record?.tteamSale?.id,
                          teamTypeId: record?.tteamSale?.teamType,
                        }
                      : {
                          teamId: undefined,
                          teamTypeId: TEAM_TYPE.stay,
                        }),
                    isAdd: false,
                  },
                });
              }}
            >
              <EyeIcon />
            </ButtonAction>
          </Space>
        ),
      },
    ];

    const exportExcel = () => {
      const exportColumns = [
        { title: 'STT', dataIndex: 'index', width: 50 },
        { title: 'Mã THTT', dataIndex: 'id', width: 100 },
        {
          title: 'Khách hàng',
          dataIndex: ['tcustomer', 'sapCustomerId'],
          width: 150,
        },
        {
          title: 'Tên Khách hàng',
          dataIndex: ['tcustomer', 'storeName'],
          width: 180,
        },
        {
          title: 'Mã KH cha',
          dataIndex: ['tcustomer', 'mainCustomerId'],
          width: 150,
        },
        { title: 'Loại đội', dataIndex: 'teamTypeFormatted', width: 100 },
        { title: 'Mã đội', dataIndex: ['tteamSale', 'subId'], width: 100 },
        { title: 'Tên đội', dataIndex: ['tteamSale', 'name'], width: 150 },
        { title: 'Giá bóng', dataIndex: 'marketPriceFormatted', width: 100 },
        {
          title: 'Tổng thanh toán',
          dataIndex: 'finalPriceFormatted',
          width: 150,
        },
        { title: 'KH ghi nợ', dataIndex: 'customerDebitId', width: 100 },
        {
          title: 'KH nợ:Q10',
          dataIndex: 'paymentCustomerDebitQ10Formatted',
          width: 100,
        },
        {
          title: 'KH nợ: Tiền mặt',
          dataIndex: 'paymentPnjpDebitAmountFormatted',
          width: 170,
        },
        {
          title: 'PNJ nợ: Q10',
          dataIndex: 'paymentPnjpDebitQ10Formatted',
          width: 130,
        },
        {
          title: 'PNJ nợ: Tiền mặt',
          dataIndex: 'pnjpDebitAmountFormatted',
          width: 170,
        },
        { title: 'Chi nhánh', dataIndex: ['tplant', 'id'], width: 100 },
        { title: 'Trạng thái', dataIndex: ['tstatus', 'name'], width: 100 },
        {
          title: 'Người tạo',
          dataIndex: ['tcreateUser', 'fullName'],
          width: 170,
        },
        { title: 'Ngày tạo', dataIndex: 'createDateFormatted', width: 170 },
        {
          title: 'Người cập nhật',
          dataIndex: ['tmodifyUser', 'fullName'],
          width: 170,
        },
        {
          title: 'Ngày cập nhật',
          dataIndex: 'modifyDateFormatted',
          width: 170,
        },
      ];

      refetch(true, exportColumns);
    };

    useImperativeHandle(ref, () => ({
      exportExcel,
    }));

    return (
      <Content>
        <Form form={form}>
          <TableComponent
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={data.items || []}
            rowKey={(record: IBillPaymentRecord) =>
              record?.id || generateUUID()
            }
            loading={loading}
            pagination={{
              current: data.pageInfo?.currentPage || 1,
              pageSize: data.pageInfo?.itemsPerPage || 20,
              total: data.pageInfo?.totalItems || 0,
              onChange: HocChangePagination(),
            }}
          />
        </Form>
      </Content>
    );
  },
);

export default memo(TablePaymentSummaryV2);
